// ReportPageBadge — a single paginated report page's own live page number
// (story 4-108; P06 §4). One more "report-render layout piece with no
// prior design-system home" in the same family as `ReportCoverPage`/
// `ReportLetterhead` (see `components/ui/index.ts`'s header comment for
// that family) — the visual chrome `PagedReportPreview.tsx`
// (`modules/reports/components/`) plants onto each real page fragment
// Paged.js produces, via a `ReactDOM.createPortal` per page (see that
// file's own header comment for the full adapter-boundary reasoning: this
// component is genuinely React, galleried and a11y-passed like any other
// primitive, even though its mount point is a DOM node an external
// pagination engine — not this app's own render tree — created).
//
// ── Why real text, not CSS `content: counter(page)` ─────────────────────
// CSS Paged Media (and Paged.js's own polyfill of it) supports a native
// `@page { @bottom-center { content: counter(page); } }` margin-box page
// number — but generated `content` text is inconsistently exposed to
// screen readers across browsers/AT combinations, and P06 §4's own
// accessibility bar for this epic (4-107's own "Accessibility" section)
// treats "announced, real content" as the baseline. A real `<span>` with
// real text costs nothing extra here (the page fragment is real DOM,
// sharing this app's own stylesheet — unlike the backend PDF twin, which
// has to fight a genuinely separate Puppeteer print-frame context) and is
// unambiguously readable by any AT.
//
// ── Front matter shows no badge at all ───────────────────────────────────
// `PagedReportPreview` never mounts this component onto a front-matter page
// (the cover) — "front matter does not consume a body page number" means
// front matter shows no number, not a badge reading "Page 0". That
// decision lives in the caller, not here: this component only ever renders
// the number it is given.
export interface ReportPageBadgeProps {
  /** 1-based body page number (front matter is never passed to this component — see header comment). */
  pageNumber: number;
  /** Total BODY pages (front matter excluded from the count, same convention as `pageNumber`). */
  totalPages: number;
  className?: string;
}

export function ReportPageBadge({ pageNumber, totalPages, className = '' }: ReportPageBadgeProps): JSX.Element {
  return (
    <span
      className={`report-page-badge pointer-events-none inline-flex select-none items-center gap-1 rounded-full border border-border bg-card/95 px-2.5 py-0.5 text-caption font-medium text-muted-foreground shadow-sm ${className}`}
    >
      {`Page ${pageNumber} of ${totalPages}`}
    </span>
  );
}
